/**
 * The persisted colour scheme (prompt.md §40).
 *
 * One hook shared by web and desktop: the product ships the jetblack dark scheme
 * by default, and an explicit choice is persisted so a reload restores it. The
 * choice is written both to `localStorage` and to the document root's
 * `data-dayvise-scheme` attribute, which is what the generated token stylesheet
 * keys the white/light scheme off.
 */

import { useEffect, useState } from "react";

export type ThemeScheme = "dark" | "light";

export const THEME_STORAGE_KEY = "dayvise.scheme";

function readStoredScheme(): ThemeScheme {
  try {
    if (typeof localStorage === "undefined") return "dark";
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {
    // Storage can be unavailable (private mode, sandboxed shell): fall back to
    // the shipped default rather than failing to render.
  }
  return "dark";
}

export function useThemeScheme(): readonly [ThemeScheme, (next: ThemeScheme) => void] {
  const [scheme, setScheme] = useState<ThemeScheme>(readStoredScheme);

  useEffect(() => {
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(THEME_STORAGE_KEY, scheme);
    } catch {
      // Persistence is best-effort; the in-memory scheme still applies.
    }
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-dayvise-scheme", scheme);
    }
  }, [scheme]);

  return [scheme, setScheme] as const;
}
